import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { UtilityService } from 'app/services/utility.service';
import { environment } from 'environments/environment';
import { ProjectCostView } from 'app/models/form/ProjectCostView';
import { AdditionFormView } from 'app/models/capital-asset/AdditionFormView';

@Injectable({
	providedIn: 'root',
})
export class ProjectManagementFormService {
	private readonly urlPath = 'project-mgt';

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, form: any): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`, this.setupRequest(form))
			.pipe();
	}

	updateForm(clientId: string, formId: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}`, this.setupRequest(request))
			.pipe();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('Application Form', request.applicationForm);
		formData.append('Project Agreement', request.projectAgreement);
		formData.append('model', JSON.stringify(request));

		return formData;
	}

	createReport(clientId: string, form: any): Observable<any> {
		return this.httpClient
			.post<any>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/save-quarter-report`,
				this.setupRrportRequest(form)
			)
			.pipe();
	}
	private setupRrportRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('Rreport', request.reportFile);
		formData.append('model', JSON.stringify(request));

		return formData;
	}
	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`).pipe();
	}

	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`, { userId })
			.pipe();
	}

	sendBack(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`, {})
			.pipe();
	}

	uploadFile(clientId: string, id, file: File, documentType = 'file'): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append(documentType, file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	getAll(clientId: string, status = 'Approved'): Observable<any[]> {
		let url = `${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}?status=${status}`;
		return this.httpClient.get<any[]>(url).pipe();
	}

	addCost(clientId: string, projectId: string, cost: ProjectCostView): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${projectId}/costs`, cost)
			.pipe();
	}

	updateCost(clientId: string, projectId: string, cost: ProjectCostView): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${projectId}/costs/${cost.id}`, cost)
			.pipe();
	}

	completeProject(clientId: string, formId: string): Observable<AdditionFormView> {
		return this.httpClient
			.post<AdditionFormView>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/complete`, {})
			.pipe();
	}
	removeDocument(clientId: string, formId: string, fileId: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/deletedocs/${fileId}`)
			.pipe();
	}

	exportPayments(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/export-payments`, {
				responseType: 'blob',
			})
			.pipe();
	}
	exportFunding(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/export-funding`, {
				responseType: 'blob',
			})
			.pipe();
	}
	getPaymentsReport(clientId: string): Observable<any[]> {
		let url = `${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/payment-report`;
		return this.httpClient.get<any[]>(url).pipe();
	}

	getFundingReport(clientId: string): Observable<any[]> {
		let url = `${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/funding-report`;
		return this.httpClient.get<any[]>(url).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`, {
				userId,
			})
			.pipe();
	}
	exportProjectReport(clientId: string, filterProjectNames: any): any {
		return this.httpClient
			.post(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/export-project-report/`,
				filterProjectNames,
				{
					responseType: 'blob',
				}
			)
			.pipe();
	}

	exportMonthlyPayments(clientId: string, year: any, month: any): any {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/export-monthly-report/${year}/${month}`, {
				responseType: 'blob',
			})
			.pipe();
	}
	exportToPdf(clientId: string, formId: string): any {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/export-payment-fundings-pdf`, {
				responseType: 'blob',
			})
			.pipe();
	}
}
